import { useRef, useState } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { trpc } from '@/config/trpc'
import { uploadToS3 } from '@/lib/utils'
import { toast } from 'sonner'

/**
 * Caption-file uploader for LinkedIn + YouTube video posts.
 *
 * Writes the selected SRT/VTT/SBV to the workspace's S3 scope so the file
 * survives reloads (workspace-id-keyed folder), and tracks both the public
 * URL and the S3 key in caller state. The key is used to delete the file
 * from S3 on replace/remove — without it we'd accumulate orphan SRTs every
 * time the user changed their mind.
 *
 * The caller persists the {url, key} pair via `platformSettings` on the
 * workspace, so the file remains addressable across reloads. When the
 * workspace itself is deleted, the entire workspace S3 folder is wiped
 * (see `workspace.delete` route), cleaning these up alongside media.
 */
type Props = {
  /** Current persisted URL (empty string = no file selected). */
  url: string
  /** Current persisted S3 key (empty string = no file selected). */
  key_: string
  workspaceId: number | undefined
  /** Called atomically with both fields whenever the selection changes.
   *  Pass empty strings to clear. */
  onChange: (next: { url: string; key: string }) => void
  /** Accepted file extensions, comma-separated. Defaults to SRT/VTT/SBV. */
  accept?: string
  /** Optional id passed to the underlying file input for label/htmlFor wiring. */
  inputId?: string
}

export function SrtUploader({
  url,
  key_,
  workspaceId,
  onChange,
  accept = '.srt,.vtt,.sbv',
  inputId,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [lastFilename, setLastFilename] = useState<string | null>(null)

  const { mutateAsync: getUploadUrl } = trpc.asset.getUploadUrl.useMutation()
  const { mutateAsync: deleteAssets } = trpc.asset.delete.useMutation()

  const triggerPicker = () => fileInputRef.current?.click()

  const upload = async (file: File) => {
    if (!workspaceId) {
      toast.error('Workspace not loaded yet — try again in a moment.')
      return
    }
    setIsUploading(true)
    try {
      // Delete the previous file first so we don't accumulate orphans when
      // the user replaces. Best-effort: a deletion failure shouldn't block
      // the new upload (the old key just lingers — workspace deletion will
      // sweep it).
      if (key_) {
        try {
          await deleteAssets({ keys: [key_] })
        } catch (deleteErr) {
          console.warn('[srt-uploader] previous file delete failed', deleteErr)
        }
      }

      const { key, uploadUrl, assetUrl } = await getUploadUrl({
        type: 'workspace',
        workspaceId,
      })
      await uploadToS3(uploadUrl, file)
      setLastFilename(file.name)
      onChange({ url: assetUrl, key })
    } catch (err) {
      console.error('[srt-uploader] upload failed', err)
      toast.error('Failed to upload caption file. Please try again.')
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const remove = async () => {
    if (!key_) {
      onChange({ url: '', key: '' })
      return
    }
    try {
      await deleteAssets({ keys: [key_] })
    } catch (err) {
      console.warn('[srt-uploader] remove delete failed', err)
    }
    setLastFilename(null)
    onChange({ url: '', key: '' })
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) await upload(file)
  }

  const displayName = lastFilename ?? (key_ ? key_.split('/').pop() || 'Caption file' : null)

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleFileChange}
      />
      {url ? (
        <div className="border-border bg-muted flex items-center justify-between gap-2 rounded-md border px-3 py-2">
          {/* `min-w-0` so `truncate` actually applies: without it this flex
              child keeps its intrinsic width and a long filename widens the
              whole settings dialog instead of ellipsising. */}
          <span className="min-w-0 truncate text-sm" title={displayName ?? undefined}>
            {displayName ?? 'Caption file uploaded'}
          </span>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={triggerPicker}
              disabled={isUploading}>
              {isUploading ? 'Uploading…' : 'Replace'}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={remove} disabled={isUploading}>
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={triggerPicker}
          disabled={isUploading}
          className="self-start">
          {isUploading ? 'Uploading…' : 'Choose caption file…'}
        </Button>
      )}
    </div>
  )
}
